import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CallHistoryTable from './CallHistoryTable';
import SearchableDropdown from './common/SearchableDropdown';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  AppPageShell,
  AppDateField,
  AppFormTextField,
  AppLoadingState,
  Button,
  EmptyState,
  PageHeader,
  Panel,
} from './common/ui';
import { isoDateStringToDate } from './common/dateFieldUtils';

const CallHistory = () => {
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stoppingCalls, setStoppingCalls] = useState(false);
  const [error, setError] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    dateRange: {
      start: '',
      end: '',
    },
    status: 'all',
    direction: 'all',
    contact: 'all',
  });

  // Fetch both calls and contacts on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [callsData, contactsData] = await Promise.all([
          api.getCalls(),
          api.request('/contacts'),
        ]);
        
        if (callsData && Array.isArray(callsData.calls)) {
          setCalls(callsData.calls);
          setFilteredCalls(callsData.calls);
          // Filter out active calls (not completed)
          const active = callsData.calls.filter(call => !call.completed);
          setActiveCalls(active);
        } else if (Array.isArray(callsData)) {
          setCalls(callsData);
          setFilteredCalls(callsData);
          // Filter out active calls (not completed)
          const active = callsData.filter(call => !call.completed);
          setActiveCalls(active);
        }

        if (Array.isArray(contactsData)) {
          setContacts(contactsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply filters whenever filters change
  useEffect(() => {
    const applyFilters = () => {
      let result = [...calls];

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        result = result.filter(call => {
          // Basic fields
          const basicFieldsMatch = [
            call.call_id,
            call.c_id,
            call.to,
            call.from,
            call.notes,
            call.summary,
            call.direction,
            call.status,
            call.call_ended_by,
            call.answered_by,
            call.transferred_to,
            call.batch_id,
            call.error_message,
            call.pathway_id,
            call.campaign_id
          ].some(field => field?.toString().toLowerCase().includes(searchLower));

          // Check variables object
          const variablesMatch = call.variables && Object.values(call.variables).some(value => 
            value?.toString().toLowerCase().includes(searchLower)
          );

          // Check pathway tags array
          const pathwayTagsMatch = call.pathway_tags?.some(tag => 
            tag?.toString().toLowerCase().includes(searchLower)
          );

          // Check metadata object
          const metadataMatch = call.metadata && Object.values(call.metadata).some(value => 
            value?.toString().toLowerCase().includes(searchLower)
          );

          // Check analysis object if it exists and is an object
          const analysisMatch = call.analysis && typeof call.analysis === 'object' && 
            Object.values(call.analysis).some(value => 
              value?.toString().toLowerCase().includes(searchLower)
            );

          return basicFieldsMatch || variablesMatch || pathwayTagsMatch || metadataMatch || analysisMatch;
        });
      }

      // Apply date range filter
      if (filters.dateRange.start) {
        result = result.filter(call => 
          new Date(call.created_at) >= new Date(filters.dateRange.start)
        );
      }
      if (filters.dateRange.end) {
        result = result.filter(call => 
          new Date(call.created_at) <= new Date(filters.dateRange.end)
        );
      }

      // Apply status filter
      if (filters.status !== 'all') {
        result = result.filter(call => 
          filters.status === 'completed' ? call.completed : !call.completed
        );
      }

      // Apply direction filter
      if (filters.direction !== 'all') {
        result = result.filter(call => {
          // If call has a direction property, use that
          if (call.direction) {
            return call.direction.toLowerCase() === filters.direction.toLowerCase();
          }
          // Otherwise, use the inbound boolean
          return call.inbound === (filters.direction.toLowerCase() === 'inbound');
        });
      }

      // Apply contact filter
      if (filters.contact !== 'all') {
        const normalizePhoneNumber = (phone) => {
          if (!phone) return '';
          // Remove any non-digit characters except '+'
          const cleaned = phone.replace(/[^\d+]/g, '');
          // Ensure it starts with '+' if it doesn't already
          return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
        };

        const selectedPhone = normalizePhoneNumber(filters.contact);
        result = result.filter(call => {
          const toNumber = normalizePhoneNumber(call.to);
          const fromNumber = normalizePhoneNumber(call.from);
          return toNumber === selectedPhone || fromNumber === selectedPhone;
        });
      }

      setFilteredCalls(result);
    };

    applyFilters();
  }, [filters, calls]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleContactSelect = (contact) => {
    handleFilterChange('contact', contact.phone_number);
  };

  const selectedContact = contacts.find(contact => contact.phone_number === filters.contact);

  // Handle stopping all active calls
  const stopAllActiveCalls = async () => {
    try {
      setStoppingCalls(true);
      const response = await api.stopAllActiveCalls();
      
      if (response.status === 'success') {
        toast.success(`Stopping ${response.num_calls} active calls`);
        // Update active calls list
        setActiveCalls([]);
        // Refresh the call list after a brief delay to allow the backend to update
        setTimeout(() => {
          api.getCalls().then(callsData => {
            if (callsData && Array.isArray(callsData.calls)) {
              setCalls(callsData.calls);
              setFilteredCalls(callsData.calls);
            } else if (Array.isArray(callsData)) {
              setCalls(callsData);
              setFilteredCalls(callsData);
            }
          });
        }, 3000);
      } else {
        toast.error('Failed to stop active calls');
      }
    } catch (error) {
      console.error('Error stopping calls:', error);
      toast.error(error.message || 'Failed to stop active calls');
    } finally {
      setStoppingCalls(false);
    }
  };

  // Handle stopping a single call
  const stopCall = async (callId) => {
    try {
      setActiveCalls(prev => prev.map(call => 
        call.call_id === callId ? { ...call, stopping: true } : call
      ));
      
      const response = await api.stopCall(callId);
      
      if (response.status === 'success') {
        toast.success('Call stopped successfully');
        // Remove the call from active calls
        setActiveCalls(prev => prev.filter(call => call.call_id !== callId));
        // Refresh the call list after a brief delay
        setTimeout(() => {
          api.getCalls().then(callsData => {
            if (callsData && Array.isArray(callsData.calls)) {
              setCalls(callsData.calls);
              setFilteredCalls(callsData.calls);
            } else if (Array.isArray(callsData)) {
              setCalls(callsData);
              setFilteredCalls(callsData);
            }
          });
        }, 2000);
      } else {
        toast.error('Failed to stop call');
        // Reset the stopping state
        setActiveCalls(prev => prev.map(call => 
          call.call_id === callId ? { ...call, stopping: false } : call
        ));
      }
    } catch (error) {
      console.error(`Error stopping call ${callId}:`, error);
      toast.error(error.message || 'Failed to stop call');
      // Reset the stopping state
      setActiveCalls(prev => prev.map(call => 
        call.call_id === callId ? { ...call, stopping: false } : call
      ));
    }
  };

  if (loading) {
    return <AppLoadingState layout="section" showLabel={false} />;
  }

  if (error) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-red-600 dark:text-red-400 px-4 text-center">
        {error}
      </div>
    );
  }

  return (
    <AppPageShell maxWidthClass="max-w-[1500px]" noXPad>
      <PageHeader
        className="mx-2 sm:mx-4"
        eyebrow="Calls"
        title="Call History"
        description="Search the ledger, refine by time and routing, then open a call for full detail, recording, and metadata."
        actions={<Button to="/app/send-call">New Call</Button>}
      />

        {activeCalls.length > 0 && (
          <div className="mt-6">
            <Panel className="p-0 overflow-hidden border-[var(--app-border)]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-5 py-4 border-b border-[var(--app-border)] bg-[var(--app-accent-soft)]">
                <div>
                  <h2 className="text-base font-bold text-[var(--app-accent-strong)]">
                    Live calls
                  </h2>
                  <p className="text-sm text-[var(--app-muted)] mt-0.5">
                    {activeCalls.length} in progress — stop individually or all at once.
                  </p>
                </div>
                <Button
                  onClick={stopAllActiveCalls}
                  disabled={stoppingCalls}
                  variant="danger"
                  className="shrink-0"
                >
                  {stoppingCalls ? 'Stopping…' : 'Stop all active'}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[var(--app-table-head)] border-b border-[var(--app-border)]">
                      <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">Call</th>
                      <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">To</th>
                      <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">From</th>
                      <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">Started</th>
                      <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">Duration</th>
                      <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCalls.map((call) => (
                      <tr key={call.call_id} className="border-b border-[var(--app-border)] hover:bg-[var(--app-table-row-hover)] transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Link
                            to={`/app/calls/${call.call_id}`}
                            className="font-mono text-xs font-semibold text-[var(--app-accent)] hover:underline"
                          >
                            {call.call_id.substring(0, 10)}…
                          </Link>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--app-ink)] max-w-[10rem] truncate" title={call.to || ''}>
                          {call.to || '—'}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[var(--app-ink)] max-w-[10rem] truncate" title={call.from || ''}>
                          {call.from || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[var(--app-muted)] tabular-nums">
                          {new Date(call.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[var(--app-muted)]">
                          {call.call_length ? `${call.call_length.toFixed(1)} min` : 'In progress'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            onClick={() => stopCall(call.call_id)}
                            disabled={call.stopping}
                            variant="danger"
                            size="sm"
                          >
                            {call.stopping ? 'Stopping…' : 'Stop'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        <div className="mt-6">
          <Panel className="p-0 overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--app-border)]">
              <h2 className="text-sm font-bold text-[var(--app-accent-strong)]">Filters</h2>
              <p className="text-xs text-[var(--app-muted)] mt-0.5">
                Narrow the list below. Table columns have their own quick filters.
              </p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-12 gap-5">
                <div className="sm:col-span-2 xl:col-span-4 space-y-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">
                    Search
                  </label>
                  <AppFormTextField
                    placeholder="Call ID, phone, notes, errors…"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    inputProps={{ 'aria-label': 'Search calls' }}
                  />
                </div>

                <div className="space-y-2 sm:col-span-1 xl:col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">
                    From date
                  </label>
                  <AppDateField
                    value={filters.dateRange.start}
                    onChange={(v) =>
                      handleFilterChange('dateRange', { ...filters.dateRange, start: v })
                    }
                    maxDate={isoDateStringToDate(filters.dateRange.end) || undefined}
                    ariaLabel="From date"
                  />
                </div>

                <div className="space-y-2 sm:col-span-1 xl:col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">
                    To date
                  </label>
                  <AppDateField
                    value={filters.dateRange.end}
                    onChange={(v) =>
                      handleFilterChange('dateRange', { ...filters.dateRange, end: v })
                    }
                    minDate={isoDateStringToDate(filters.dateRange.start) || undefined}
                    ariaLabel="To date"
                  />
                </div>

                <div className="space-y-2 sm:col-span-1 xl:col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">
                    Status
                  </label>
                  <SearchableDropdown
                    options={[
                      { id: 'all', name: 'All Statuses', value: 'all' },
                      { id: 'completed', name: 'Completed', value: 'completed' },
                      { id: 'in_progress', name: 'In Progress', value: 'in_progress' }
                    ]}
                    onSelect={(option) => handleFilterChange('status', option.value)}
                    selectedOption={
                      [
                        { id: 'all', name: 'All Statuses', value: 'all' },
                        { id: 'completed', name: 'Completed', value: 'completed' },
                        { id: 'in_progress', name: 'In Progress', value: 'in_progress' }
                      ].find(option => option.value === filters.status)
                    }
                    placeholder="Filter by status"
                    searchPlaceholder="Search statuses..."
                    displayKey="name"
                  />
                </div>

                <div className="space-y-2 sm:col-span-1 xl:col-span-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">
                    Direction
                  </label>
                  <SearchableDropdown
                    options={[
                      { id: 'all', name: 'All Directions', value: 'all' },
                      { id: 'inbound', name: 'Inbound', value: 'inbound' },
                      { id: 'outbound', name: 'Outbound', value: 'outbound' }
                    ]}
                    onSelect={(option) => handleFilterChange('direction', option.value)}
                    selectedOption={
                      [
                        { id: 'all', name: 'All Directions', value: 'all' },
                        { id: 'inbound', name: 'Inbound', value: 'inbound' },
                        { id: 'outbound', name: 'Outbound', value: 'outbound' }
                      ].find(option => option.value === filters.direction)
                    }
                    placeholder="Filter by direction"
                    searchPlaceholder="Search directions..."
                    displayKey="name"
                  />
                </div>

                <div className="space-y-2 sm:col-span-2 xl:col-span-4">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--app-muted)]">
                    Contact
                  </label>
                  <SearchableDropdown
                    options={[{ id: 'all', name: 'All Contacts', phone_number: 'all' }, ...contacts]}
                    onSelect={(contact) => handleContactSelect(contact)}
                    selectedOption={selectedContact || { id: 'all', name: 'All Contacts', phone_number: 'all' }}
                    placeholder="All contacts"
                    searchPlaceholder="Search by name or number…"
                    displayKey="name"
                    secondaryDisplayKey="phone_number"
                    renderOption={(contact) =>
                      contact.id === 'all' ? (
                        <div className="text-base font-medium text-gray-900 dark:text-slate-100">All contacts</div>
                      ) : (
                        <div>
                          <div className="text-base font-medium text-gray-900 dark:text-slate-100">
                            {contact.name || 'Unnamed'}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-slate-400">{contact.phone_number}</div>
                          {contact.email && (
                            <div className="text-sm text-gray-500 dark:text-slate-500">{contact.email}</div>
                          )}
                          {contact.company && (
                            <div className="text-sm text-gray-500 dark:text-slate-500">{contact.company}</div>
                          )}
                        </div>
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </Panel>

          <div className="mt-6">
            {filteredCalls.length === 0 ? (
              <EmptyState
                title={calls.length === 0 ? 'No calls yet' : 'No matching calls'}
                description={calls.length === 0 ? 'Outbound and inbound calls will show up here once activity starts.' : 'Try clearing search, widening the date range, or resetting column filters in the table headers.'}
              />
            ) : (
              <CallHistoryTable calls={filteredCalls} />
            )}
          </div>
        </div>
    </AppPageShell>
  );
};

export default CallHistory; 
